// src/components/TableFilter.tsx
import React, { ReactNode, useState } from "react";
import { PiMagnifyingGlassBold, PiFunnel, PiXBold } from "react-icons/pi";
import { Button, ActionIcon, Input, Title, Drawer } from "rizzui";
import cn from "@core/utils/class-names";
import { useMedia } from "@core/hooks/use-media";
import { ToggleColumns } from "@core/components/legacy-table";

/**
 * Props for the filter drawer itself
 */
interface FilterDrawerViewProps {
  isOpen: boolean;
  drawerTitle?: string;
  setOpenDrawer: (open: boolean) => void;
  children?: ReactNode;
}

/**
 * A sliding drawer that holds your filter inputs
 */
export function FilterDrawerView({
  isOpen,
  drawerTitle = "Filters",
  setOpenDrawer,
  children,
}: FilterDrawerViewProps) {
  return (
    <Drawer
      size="sm"
      isOpen={isOpen}
      onClose={() => setOpenDrawer(false)}
      overlayClassName="dark:bg-black/40 dark:backdrop-blur-lg opacity-30"
      containerClassName="dark:bg-gray-100"
      className="z-[9999]"
    >
      <div className="flex h-full flex-col p-5">
        <div className="-mx-5 mb-6 flex items-center justify-between border-b border-muted px-5 pb-4">
          <Title as="h5">{drawerTitle}</Title>
          <ActionIcon
            size="sm"
            rounded="full"
            variant="text"
            title="Close filters"
            onClick={() => setOpenDrawer(false)}
          >
            <PiXBold className="h-4 w-4" />
          </ActionIcon>
        </div>
        <div className="flex-grow overflow-auto">
          <div className="grid grid-cols-1 gap-6 [&_.price-field>span.mr-2]:mb-1.5 [&_.price-field]:flex-col [&_.price-field]:items-start [&_.react-datepicker-wrapper]:w-full [&_.text-gray-500]:text-gray-700 [&_button.h-9]:h-10 sm:[&_button.h-9]:h-11 [&_label>.h-9]:h-10 sm:[&_label>.h-9]:h-11 [&_label>.w-24.h-9]:w-full">
            {children}
          </div>
        </div>
        <Button
          size="lg"
          onClick={() => setOpenDrawer(false)}
          className="mt-5 h-11 w-full text-sm"
        >
          Show Results
        </Button>
      </div>
    </Drawer>
  );
}

/**
 * Props for the top-level TableFilter component
 */
export interface TableFilterProps {
  /** current string in the search box */
  searchTerm: string;
  /** called when the user clicks the little “×” in the search input */
  onSearchClear: () => void;
  /** called on every keystroke in the search input */
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /** an array of your column definitions for ToggleColumns */
  columns: { [key: string]: any }[];
  /** which columns are currently visible */
  checkedColumns: string[];
  setCheckedColumns: (cols: string[]) => void;
  /** optional index column to hide */
  hideIndex?: number;
  /** whatever filter controls (e.g. dropdowns) go here */
  children?: ReactNode;
  /** override the drawer’s heading */
  drawerTitle?: string;
  /** if true, show the search input on the right side instead */
  showSearchOnTheRight?: boolean;
  /** if true, force a drawer even on wide screens */
  enableDrawerFilter?: boolean;
  /** a custom “more filters” menu button */
  menu?: ReactNode;
}

/**
 * A flexible search/filter bar with optional drawer on smaller screens
 */
export default function TableFilter({
  searchTerm,
  onSearchClear,
  onSearchChange,
  columns,
  checkedColumns,
  setCheckedColumns,
  hideIndex,
  children,
  drawerTitle = "Table Filters",
  showSearchOnTheRight = false,
  enableDrawerFilter = false,
  menu,
}: TableFilterProps) {
  const isMediumScreen = useMedia("(max-width: 1860px)", false);
  const [showFilters, setShowFilters] = useState(true);
  const [openDrawer, setOpenDrawer] = useState(false);

  return (
    <div className="table-filter mb-4 flex items-center justify-between">
      <div className="flex flex-wrap items-center gap-4">
        {!showSearchOnTheRight && (
          <Input
            type="search"
            placeholder="Search by anything..."
            value={searchTerm}
            onClear={onSearchClear}
            onChange={onSearchChange}
            inputClassName="h-9"
            clearable
            prefix={<PiMagnifyingGlassBold className="h-4 w-4" />}
          />
        )}

        {showSearchOnTheRight && enableDrawerFilter && menu}

        {children && (
          <>
            {isMediumScreen || enableDrawerFilter ? (
              <FilterDrawerView
                isOpen={openDrawer}
                setOpenDrawer={setOpenDrawer}
                drawerTitle={drawerTitle}
              >
                {children}
              </FilterDrawerView>
            ) : (
              showFilters && <>{children}</>
            )}
          </>
        )}
      </div>

      <div className="ms-4 flex flex-shrink-0 items-center">
        {showSearchOnTheRight && (
          <Input
            type="search"
            placeholder="Search by anything..."
            value={searchTerm}
            onClear={onSearchClear}
            onChange={onSearchChange}
            inputClassName="h-9"
            clearable
            prefix={<PiMagnifyingGlassBold className="h-4 w-4" />}
            className="me-2.5"
          />
        )}

        {children && (
          <Button
            variant="outline"
            onClick={() =>
              isMediumScreen || enableDrawerFilter
                ? setOpenDrawer(!openDrawer)
                : setShowFilters(!showFilters)
            }
            className={cn(
              "me-2.5 h-9 pe-3 ps-2.5",
              !isMediumScreen &&
                !enableDrawerFilter &&
                showFilters &&
                "border-dashed border-gray-700"
            )}
          >
            <PiFunnel className="me-1.5 h-[18px] w-[18px]" strokeWidth={1.7} />
            {isMediumScreen || enableDrawerFilter
              ? "Filters"
              : showFilters
                ? "Hide Filters"
                : "Filters"}
          </Button>
        )}

        <ToggleColumns
          columns={columns}
          checkedColumns={checkedColumns}
          setCheckedColumns={setCheckedColumns}
          hideIndex={hideIndex}
        />
      </div>
    </div>
  );
}
